/**
 * useJobForm - Hook for JobFormModal
 *
 * Manages:
 * - Form state and initialization
 * - Data loading (hospitals, clientes, operators, locations)
 * - Duration calculation
 * - Submit handler with Epic 46 intervalo validation
 * - Epic 47 justificativas support
 * - Save execution logic
 */
import React, { useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import jobService from '../services/jobService';
import clienteService from '../services/clienteService';
import type { ValidarIntervaloResponse } from '../services/clienteService';
import { msg } from '../utils/messages';
import { JustificativaIntervalo } from '../components/IntervaloAgendamentoModal';
import { computeHoraFim59min } from '../utils/duracao';
import { clientesParaValidarIntervalo } from '../utils/intervaloAgendamento';

export const jobSchema = z.object({
  hospital_id: z.number().min(1, 'Hospital é obrigatório'),
  cliente_ids: z.array(z.number()).min(1, 'Pelo menos um cliente é obrigatório'),
  location_id: z.number().min(1, 'Localização é obrigatória'),
  operador_id: z.number().min(1, 'Operador é obrigatório'),
  titulo: z.string().min(3, 'Título deve ter no mínimo 3 caracteres'),
  descricao: z.string().optional(),
  data_prevista: z.string().min(1, 'Data prevista é obrigatória'),
  hora_inicio: z.string().optional(),
  hora_fim: z.string().optional(),
  status: z.string().optional(),
});

interface UseJobFormProps {
  job?: any | null;
  onClose: (shouldReload: boolean) => void;
  onSuccess: (message: string) => void;
}

export function useJobForm({ job, onClose, onSuccess }: UseJobFormProps) {
  // hora_fim default = hora_inicio + 29min (padrao; computeHoraFim59min usa a constante)
  const horaInicioInicial = job?.hora_inicio || '07:00';
  const [formData, setFormData] = useState({
    hospital_id: job?.hospital_id || '',
    cliente_ids: job?.clientes?.map((c: any) => c.id) || (job?.cliente_id ? [job.cliente_id] : []),
    location_id: job?.location_id || '',
    operador_id: job?.operador_id || '',
    titulo: job?.titulo || '',
    descricao: job?.descricao || '',
    data_prevista: job?.data_prevista || '',
    hora_inicio: horaInicioInicial,
    hora_fim: job?.hora_fim || computeHoraFim59min(horaInicioInicial),
    status: job?.status || 'AGENDADA',
  });

  const [hospitals, setHospitals] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Locais de contagem (editável)
  const [allHospitalLocais, setAllHospitalLocais] = useState<{id: number; nome: string; responsavel: string}[]>([]);
  const [selectedLocalIds, setSelectedLocalIds] = useState<number[]>(
    job?.locais_contagem?.map((l: any) => l.id) || []
  );
  const [loadingLocais, setLoadingLocais] = useState(false);
  const manualLocalIdsRef = useRef<Set<number>>(new Set());
  const lastAutoIdsRef = useRef<Set<number>>(new Set());
  const isEditInitializedRef = useRef(false);

  // Epic 46: Intervalo validation modal state
  const [showIntervaloModal, setShowIntervaloModal] = useState(false);
  const [intervaloValidacoes, setIntervaloValidacoes] = useState<ValidarIntervaloResponse[]>([]);
  const [intervaloLoading, setIntervaloLoading] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any>(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load locations when hospital changes
  useEffect(() => {
    if (formData.hospital_id) {
      loadLocations(Number(formData.hospital_id));
    } else {
      setLocations([]);
      setFormData(prev => ({ ...prev, location_id: '' }));
    }
  }, [formData.hospital_id]);

  // Load all locais de contagem when hospital changes
  useEffect(() => {
    const hospitalId = Number(formData.hospital_id);
    if (hospitalId > 0) {
      jobService.getLocaisContagem(hospitalId)
        .then(setAllHospitalLocais)
        .catch(() => setAllHospitalLocais([]));
    } else {
      setAllHospitalLocais([]);
      setSelectedLocalIds([]);
      manualLocalIdsRef.current = new Set();
      lastAutoIdsRef.current = new Set();
    }
  }, [formData.hospital_id]);

  // Auto-compute locais de contagem when hospital or clients change
  useEffect(() => {
    const hospitalId = Number(formData.hospital_id);
    const clienteIds = formData.cliente_ids.map(Number).filter(id => id > 0);

    if (hospitalId > 0 && clienteIds.length > 0) {
      setLoadingLocais(true);
      jobService.getPreviewLocais(hospitalId, clienteIds)
        .then(autoLocais => {
          const newAutoIds = new Set(autoLocais.map(l => l.id));
          const prevAutoIds = lastAutoIdsRef.current;

          // Edit mode: detect manual locais on first load
          if (job && !isEditInitializedRef.current) {
            const existingIds: number[] = job.locais_contagem?.map((l: any) => l.id) || [];
            existingIds.forEach((id: number) => {
              if (!newAutoIds.has(id)) manualLocalIdsRef.current.add(id);
            });
            isEditInitializedRef.current = true;
          }

          setSelectedLocalIds(prev => {
            const current = new Set<number>(prev);
            // Remove auto-locais that no longer apply (client removed)
            prevAutoIds.forEach(id => {
              if (!newAutoIds.has(id) && !manualLocalIdsRef.current.has(id)) {
                current.delete(id);
              }
            });
            // Add new auto-locais
            newAutoIds.forEach(id => current.add(id));
            return Array.from(current);
          });

          lastAutoIdsRef.current = newAutoIds;
        })
        .catch(err => {
          console.warn('Erro ao carregar preview de locais:', err);
        })
        .finally(() => setLoadingLocais(false));
    } else {
      // No clients: remove auto-locais, keep manual
      setSelectedLocalIds(prev =>
        prev.filter(id => manualLocalIdsRef.current.has(id))
      );
      lastAutoIdsRef.current = new Set();
      setLoadingLocais(false);
    }
  }, [formData.hospital_id, formData.cliente_ids]);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [hospitalsData, clientesData, operatorsData] = await Promise.all([
        jobService.getHospitals(),
        jobService.getClientes(),
        jobService.getOperators(),
      ]);
      setHospitals(hospitalsData);
      setClientes(clientesData);
      setOperators(operatorsData);
      if (job?.hospital_id) {
        const locationsData = await jobService.getLocations(job.hospital_id);
        setLocations(locationsData);
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      msg.error('Erro ao carregar dados do formulário');
    } finally {
      setLoadingData(false);
    }
  };

  const loadLocations = async (hospitalId: number) => {
    try {
      const locationsData = await jobService.getLocations(hospitalId);
      setLocations(locationsData);
    } catch (error: any) {
      console.error('Erro ao carregar localizações:', error);
      setLocations([]);
    }
  };

  const calcularDuracao = (): string => {
    if (!formData.hora_inicio || !formData.hora_fim) return '';
    const [inicioH, inicioM] = formData.hora_inicio.split(':').map(Number);
    const [fimH, fimM] = formData.hora_fim.split(':').map(Number);
    const duracaoMinutos = (fimH * 60 + fimM) - (inicioH * 60 + inicioM);
    if (duracaoMinutos <= 0) return '(inválido)';
    const horas = Math.floor(duracaoMinutos / 60);
    const minutos = duracaoMinutos % 60;
    if (horas > 0 && minutos > 0) return `${horas}h ${minutos}min`;
    if (horas > 0) return `${horas}h`;
    return `${minutos}min`;
  };

  // Handler for locais de contagem manual changes
  const handleLocaisChange = (newIds: number[]) => {
    const oldSet = new Set(selectedLocalIds);
    const newSet = new Set(newIds);

    // Newly added manually (not from auto-compute)
    newIds.forEach(id => {
      if (!oldSet.has(id) && !lastAutoIdsRef.current.has(id)) {
        manualLocalIdsRef.current.add(id);
      }
    });

    // Removed: clean up manual tracking
    selectedLocalIds.forEach(id => {
      if (!newSet.has(id)) {
        manualLocalIdsRef.current.delete(id);
      }
    });

    setSelectedLocalIds(newIds);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Execute save with optional justificativas
  const executeSave = async (dataToValidate: any, justificativas?: JustificativaIntervalo[]) => {
    setLoading(true);
    try {
      const finalData = justificativas && justificativas.length > 0
        ? {
            ...dataToValidate,
            justificativas_intervalo: justificativas.map(j => ({
              cliente_id: j.cliente_id,
              tipo_violacao: j.tipo_violacao,
              justificativa: j.justificativa,
              intervalo_configurado_dias: j.intervalo_configurado_dias,
              intervalo_atual_dias: j.intervalo_atual_dias,
              data_ultima_contagem: j.data_ultima_contagem,
            })),
          }
        : dataToValidate;

      if (job) {
        await jobService.updateJob(job.id, finalData);
        onSuccess('Contagem atualizada com sucesso');
        onClose(true);
      } else {
        if (selectedFile) {
          const result = await jobService.uploadExcel(
            selectedFile,
            finalData.hospital_id,
            finalData.location_id,
            finalData.operador_id,
            formData.titulo,
            finalData.cliente_ids[0]
          );
          onSuccess(`Contagem criada com ${result.total_products || 0} produto(s) do Excel!`);
        } else {
          await jobService.createJob(finalData);
          onSuccess('Contagem criada com sucesso');
        }
        onClose(true);
      }
    } catch (error: any) {
      console.error('Erro ao salvar contagem:', error);
      msg.error(error.response?.data?.message || error.response?.data?.detail || 'Erro ao salvar contagem');
    } finally {
      setLoading(false);
    }
  };

  // Epic 46: Intervalo modal handlers
  const handleIntervaloConfirm = async () => {
    setIntervaloLoading(true);
    try {
      if (pendingSaveData) await executeSave(pendingSaveData);
    } finally {
      setIntervaloLoading(false);
      setShowIntervaloModal(false);
      setPendingSaveData(null);
    }
  };

  const handleIntervaloConfirmWithJustificativas = async (justificativas: JustificativaIntervalo[]) => {
    setIntervaloLoading(true);
    try {
      if (pendingSaveData) await executeSave(pendingSaveData, justificativas);
    } finally {
      setIntervaloLoading(false);
      setShowIntervaloModal(false);
      setPendingSaveData(null);
    }
  };

  const handleIntervaloCancel = () => {
    setShowIntervaloModal(false);
    setPendingSaveData(null);
    setIntervaloValidacoes([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try {
      const dataToValidate = {
        ...formData,
        hospital_id: Number(formData.hospital_id),
        location_id: Number(formData.location_id),
        operador_id: Number(formData.operador_id),
        cliente_ids: formData.cliente_ids.map(Number),
        local_ids: selectedLocalIds,
      };

      jobSchema.parse(dataToValidate);

      // Epic 46: Validate scheduling interval
      //
      // N165: a condição aqui só perguntava se os campos EXISTIAM — e como toda
      // gravação manda a data, toda gravação reabria o modal de intervalo (até ao
      // incluir um lacre). Agora quem decide é `clientesParaValidarIntervalo`:
      // lista vazia = nem chama a API. A mesma função governa `useContagemSave`,
      // porque este defeito nasceu justamente de a condição existir em dois lugares.
      const clientesAValidar = clientesParaValidarIntervalo({
        isNew: !job?.id,
        dataMudou: !!job?.id && (job.data_prevista || '') !== (dataToValidate.data_prevista || ''),
        hospitalMudou: !!job?.id && Number(job.hospital_id) !== Number(dataToValidate.hospital_id),
        clientesAtuais: dataToValidate.cliente_ids,
        clientesOriginais: job?.clientes?.map((c: any) => c.id) ?? [],
      });

      if (clientesAValidar.length > 0 && dataToValidate.hospital_id && dataToValidate.data_prevista) {
        setLoading(true);
        try {
          const validacao = await clienteService.validarIntervaloMultiplosClientes(
            clientesAValidar,
            dataToValidate.hospital_id,
            dataToValidate.data_prevista,
            job?.id
          );
          if (!validacao.valido && validacao.validacoes.some((v: any) => v.violacoes.length > 0)) {
            setIntervaloValidacoes(validacao.validacoes);
            setPendingSaveData(dataToValidate);
            setShowIntervaloModal(true);
            setLoading(false);
            return;
          }
        } catch (validationError: any) {
          console.warn('Erro na validação de intervalo, continuando com save:', validationError);
        }
        setLoading(false);
      }

      await executeSave(dataToValidate);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0].toString()] = err.message;
        });
        setErrors(fieldErrors);
      } else {
        console.error('Erro ao salvar contagem:', error);
        msg.error(error.response?.data?.message || error.response?.data?.detail || 'Erro ao salvar contagem');
      }
      setLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    hospitals,
    clientes,
    locations,
    operators,
    selectedFile,
    setSelectedFile,
    loading,
    loadingData,
    errors,
    setErrors,
    showIntervaloModal,
    intervaloValidacoes,
    intervaloLoading,
    allHospitalLocais,
    selectedLocalIds,
    handleLocaisChange,
    loadingLocais,
    calcularDuracao,
    handleChange,
    handleSubmit,
    handleIntervaloConfirm,
    handleIntervaloConfirmWithJustificativas,
    handleIntervaloCancel,
  };
}
